import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Send } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', email: '', phone: '', message: '', smsNotifications: false, smsMarketing: false };
const FIELD = 'bg-white/5 border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-orange-500 focus-visible:border-orange-400 h-12 rounded-xl';

const CONSENTS = [
  ['smsNotifications', 'sms-notifications', 'I consent to receive SMS notifications and alerts from Zivae Financial. These messages include appointment reminders and confirmations, order and delivery updates, service updates. Message frequency varies. Message & data rates may apply. Text HELP for assistance. You can reply STOP to unsubscribe at any time.'],
  ['smsMarketing', 'sms-marketing', 'I consent to receive marketing text messages from Zivae Financial about special offers, discounts, new product updates. Message frequency varies. Message & data rates may apply. Reply HELP for help or STOP to opt-out.'],
];

export const ConsentFields = ({ prefix, form, setForm }) => (
  <div className="space-y-3 pt-2">
    {CONSENTS.map(([field, suffix, text]) => {
      const id = `${prefix}-${suffix}`;
      return (
        <div key={id} className="flex items-start gap-3 bg-white/[0.03] border border-white/10 rounded-xl p-4">
          <input type="checkbox" id={id} data-testid={`${id}-checkbox`} checked={form[field]}
            onChange={(e) => setForm({ ...form, [field]: e.target.checked })}
            className="mt-1 h-4 w-4 shrink-0 accent-orange-500" />
          <label htmlFor={id} className="text-xs sm:text-sm text-slate-300 leading-relaxed">{text}</label>
        </div>
      );
    })}
  </div>
);

export const LegalNote = () => (
  <p className="text-sm text-slate-400 text-center">
    By submitting this form, you agree to our{' '}
    <Link to="/terms-of-service" className="text-orange-400 underline hover:text-orange-300">Terms of Service</Link> and{' '}
    <Link to="/privacy-policy" className="text-orange-400 underline hover:text-orange-300">Privacy Policy</Link>
  </p>
);

export const TextField = ({ id, label, form, setForm, field, ...rest }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-slate-200">{label}</Label>
    <Input id={id} data-testid={`${id}-input`} value={form[field]} onChange={(e) => setForm({ ...form, [field]: e.target.value })} className={FIELD} {...rest} />
  </div>
);

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setStatus('ok');
      setForm(EMPTY);
    } catch {
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="glass-panel rounded-3xl p-7 md:p-10 space-y-6">
      <div className="grid md:grid-cols-2 gap-5">
        <TextField id="contact-name" label="Name *" field="name" form={form} setForm={setForm} required placeholder="Your name" />
        <TextField id="contact-email" label="Email *" field="email" form={form} setForm={setForm} type="email" required placeholder="your@email.com" />
      </div>
      <TextField id="contact-phone" label="Phone" field="phone" form={form} setForm={setForm} type="tel" placeholder="(optional)" />
      <div className="space-y-2">
        <Label htmlFor="contact-message" className="text-slate-200">Message *</Label>
        <Textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help you?"
          className="bg-white/5 border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-orange-500 rounded-xl resize-none" />
      </div>
      <ConsentFields prefix="contact" form={form} setForm={setForm} />
      <LegalNote />
      <button type="submit" disabled={sending} data-testid="contact-submit-button"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold py-4 text-base">
        {sending ? 'Sending...' : 'Send Message'} <Send className="w-4 h-4" />
      </button>
      {status === 'ok' && <p data-testid="contact-success-message" className="text-center text-emerald-400 text-sm">Thank you for contacting us! We'll get back to you soon.</p>}
      {status === 'error' && <p data-testid="contact-error-message" className="text-center text-red-400 text-sm">Something went wrong. Please try again or call us directly.</p>}
    </form>
  );
};
